// Step 4's plan screen is the membership project's paywall and checkout, reused
// by redirect (docs/30 §6). Out: the paywall URL with the flow's parameters and
// flow=submission, which turns on its exits back to the flow (React-Membership
// #200). Back: the completion pages hand the actor back to step 4.
//
// liteOffer=1 gives the paywall its explicit "Continue with Lite version"
// button (without it, offer 1 has only a close button). sessionId only for a new
// signup (its onboarding session, from checkout-started), as the classic signup
// passes its own; no sh or hs, which belong to the membership header.

import { getSubmissionWebSessionKey } from "../../../helpers/submissionAuth";
import { STEP4_PARAM } from "./step4Variant";

// The flow's own parameters: the casting call, the arm pick, the campaign tag,
// the forced country and language. Everything else on the URL stays behind.
export const FLOW_PARAMS = ["bd", "roleid", STEP4_PARAM, "src", "ctry", "lang"];

// Set on the way back from a purchase: step 4 then waits for the plan, and in
// test A sends.
export const PAID_PARAM = "paid";

// Tests C and D: Stripe's back link to step 4 (set by the API's direct checkout).
export const CHECKOUT_PARAM = "checkout";

const copyFlowParams = (from: URLSearchParams, to: URLSearchParams) => {
  FLOW_PARAMS.forEach((name) => {
    const value = from.get(name);
    if (value) to.set(name, value);
  });
};

export const membershipPaywallUrl = (search: string, submissionId: number, sessionId?: string | null): string => {
  const base = (process.env.REACT_APP_CWB_Membership_Base || "").replace(/\/+$/, "");
  const params = new URLSearchParams();
  const key = getSubmissionWebSessionKey();
  if (key) params.set("k", key);
  params.set("flow", "submission");
  params.set("liteOffer", "1");
  copyFlowParams(new URLSearchParams(search || ""), params);
  params.set("sid", String(submissionId));
  if (sessionId) params.set("sessionId", sessionId);
  return `${base}/promo?${params.toString()}`;
};

// The step-4 search string to resume with, when a completion page was reached
// from the flow (flow=submission came back on the URL); null otherwise, and the
// page is the classic signup's own, unchanged.
export const flowReturnSearch = (search: string): string | null => {
  const incoming = new URLSearchParams(search || "");
  if (incoming.get("flow") !== "submission") return null;
  const params = new URLSearchParams();
  copyFlowParams(incoming, params);
  params.set(PAID_PARAM, "1");
  return `?${params.toString()}`;
};

export const isPaidReturn = (search: string): boolean =>
  new URLSearchParams(search || "").get(PAID_PARAM) === "1";

// Left for the plan screen, kept in the tab (sessionStorage survives the trip to the other
// site). Back on step 4 without paying (the browser's Back button) means the actor wants to
// look again: step 4 shows the review with Continue instead of sending them straight back,
// which in test B would trap them between the two pages.
const LEFT_KEY = "sfLeftForPaywall.v1";
const LEFT_FOR_MS = 2 * 60 * 60 * 1000;

export const markLeftForPaywall = (submissionId: number) => {
  try {
    window.sessionStorage.setItem(LEFT_KEY, JSON.stringify({ submissionId, at: Date.now() }));
  } catch (e) {
    // Without storage, Back from the paywall goes through to it again in test B.
  }
};

// Read once: true when this submission left for the paywall in this tab, recently.
export const consumeLeftForPaywall = (submissionId: number, nowMs: number = Date.now()): boolean => {
  try {
    const raw = window.sessionStorage.getItem(LEFT_KEY);
    window.sessionStorage.removeItem(LEFT_KEY);
    const left = raw ? JSON.parse(raw) : null;
    return !!left && left.submissionId === submissionId && nowMs - Number(left.at) < LEFT_FOR_MS;
  } catch (e) {
    return false;
  }
};
